// Theme store — light/dark toggle + persistence.
//
// The initial theme is applied before React runs by public/theme-init.js
// (external script, CSP-safe) which sets `data-theme` on <html>. This
// store mirrors that and owns the toggle: it flips the attribute and
// persists the choice. The toggle BUTTON's icon is switched purely in
// CSS off `[data-theme]` (see index.css), so there's no hydration
// mismatch between the static pre-render and the client.

import { create } from "zustand";

export type Theme = "light" | "dark";

const STORAGE_KEY = "th-theme";

function readDomTheme(): Theme {
  if (typeof document === "undefined") return "dark";
  return document.documentElement.getAttribute("data-theme") === "light"
    ? "light"
    : "dark";
}

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggle: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: readDomTheme(),
  setTheme: (theme) => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("data-theme", theme);
    }
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* storage blocked — choice won't persist, theme still applies */
    }
    set({ theme });
  },
  toggle: () => get().setTheme(get().theme === "dark" ? "light" : "dark"),
}));
